/* PPE Parts & Supply: content page scripts (contact, legal, article, document index). No dependencies.
   - [data-legal]: TOC scroll-spy. Sets aria-current="true" on [data-toc-link] whose target section is in view.
     The mobile <details data-toc-mobile> closes after a link is chosen.
   - [data-doc-index]: filters the manual list in place. Every PDF row is already in the page, so the
     search hides what does not match and opens the brands that do. No network, no index to keep in sync.
   - [data-focus-on-load]: form status messages rendered after a POST receive focus so screen readers announce them.
   - form[data-quote-endpoint]: sends the contact form, with photos, to the upload endpoint when one is set. */
(function () {
  'use strict';

  const initLegal = (root) => {
    if (!root || root.dataset.legalReady) return;
    root.dataset.legalReady = 'true';

    const links = Array.from(root.querySelectorAll('[data-toc-link]'));
    const targets = new Map();

    links.forEach((link) => {
      const id = decodeURIComponent(link.hash.slice(1));
      const target = id && document.getElementById(id);
      if (!target) return;
      if (!targets.has(target)) targets.set(target, []);
      targets.get(target).push(link);
    });

    root.querySelectorAll('details[data-toc-mobile]').forEach((details) => {
      details.addEventListener('click', (event) => {
        if (event.target.closest('a')) details.open = false;
      });
    });

    if (!targets.size || !('IntersectionObserver' in window)) return;

    const ordered = Array.from(targets.keys());
    const visible = new Set();
    let current = null;

    const setActive = (target) => {
      if (target === current) return;
      current = target;
      links.forEach((link) => link.removeAttribute('aria-current'));
      (targets.get(target) || []).forEach((link) => link.setAttribute('aria-current', 'true'));
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        const first = ordered.find((target) => visible.has(target));
        if (first) setActive(first);
      },
      { rootMargin: '-10% 0px -55% 0px', threshold: 0 }
    );

    ordered.forEach((target) => observer.observe(target));
    root._legalObserver = observer;
  };


  /* ---------- Document index search ---------- */

  /* Model numbers are written half a dozen ways across twenty years of pasted
     content: "A.B Dick 9865/9869/9870", "QP-25", "Kompac II". Folding every
     run of punctuation to a single space lets one query match all of them. */
  const normalise = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  /* Nobody types "a b dick". Matching a space-free copy of both sides as well
     means "ab dick" and "abdick" reach a page that spells it "A.B. Dick". */
  const squash = (value) => normalise(value).replace(/ /g, '');

  const initDocIndex = (root) => {
    if (!root || root.dataset.docIndexReady) return;
    const input = root.querySelector('[data-doc-search]');
    if (!input) return;
    root.dataset.docIndexReady = 'true';

    const clearButton = root.querySelector('[data-doc-clear]');
    const status = root.querySelector('[data-doc-status]');
    const countTemplate = root.dataset.countFiltered || '[n] of [t]';
    const resultsTemplate = root.dataset.results || '';
    const emptyTemplate = root.dataset.empty || '';

    const entries = Array.from(root.querySelectorAll('.doc-toggles__item')).map((item) => {
      const toggle = item.querySelector('.doc-toggle');
      const labelEl = item.querySelector('.doc-toggle__label');
      const countEl = item.querySelector('.doc-toggle__count');
      const brand = labelEl ? labelEl.textContent : '';
      const groups = Array.from(item.querySelectorAll('.doc-group')).map((el) => ({ el: el, rows: [] }));
      const files = [];

      Array.from(item.querySelectorAll('.doc-files > li')).forEach((row) => {
        const groupEl = row.closest('.doc-group');
        const group = groups.filter((candidate) => candidate.el === groupEl)[0] || null;
        const title = groupEl ? groupEl.querySelector('.doc-group__title') : null;
        const label = row.querySelector('.doc-file__label');
        /* A row is searchable by the brand and section it sits under as well as
           its own name, so "hamada 700" finds a file labelled only "700". */
        const text = [brand, title ? title.textContent : '', label ? label.textContent : ''].join(' ');
        const file = { el: row, haystack: normalise(text), squashed: squash(text) };
        if (group) group.rows.push(file);
        files.push(file);
      });

      return {
        item: item,
        toggle: toggle && toggle.tagName === 'DETAILS' ? toggle : null,
        countEl: countEl,
        countText: countEl ? countEl.textContent : '',
        brand: normalise(brand),
        brandSquashed: squash(brand),
        groups: groups,
        files: files,
      };
    });

    if (!entries.length) return;

    const setOpen = (entry, open) => {
      if (!entry.toggle) return;
      /* The exclusive-accordion name keeps one panel open at a time, which is
         the opposite of what a result list needs; it goes back on reset. */
      if (open && entry.toggle.name) {
        entry.toggle.dataset.docName = entry.toggle.name;
        entry.toggle.removeAttribute('name');
      }
      entry.toggle.open = open;
    };

    const reset = () => {
      root.classList.remove('is-filtering');
      entries.forEach((entry) => {
        entry.item.hidden = false;
        entry.item.classList.remove('is-first');
        entry.files.forEach((file) => { file.el.hidden = false; });
        entry.groups.forEach((group) => { group.el.hidden = false; });
        if (entry.countEl) entry.countEl.textContent = entry.countText;
        if (entry.toggle) entry.toggle.open = false;
      });
      entries.forEach((entry) => {
        if (entry.toggle && entry.toggle.dataset.docName) {
          entry.toggle.name = entry.toggle.dataset.docName;
          delete entry.toggle.dataset.docName;
        }
      });
      if (status) { status.hidden = true; status.textContent = ''; }
      if (clearButton) clearButton.hidden = true;
    };

    const filter = (raw) => {
      const query = normalise(raw);
      if (!query) { reset(); return; }

      const terms = query.split(' ').map((term) => ({ term: term, squashed: term.replace(/ /g, '') }));
      const hits = (haystack, squashed) => terms.every((t) => (
        haystack.indexOf(t.term) !== -1 || squashed.indexOf(t.squashed) !== -1
      ));

      root.classList.add('is-filtering');
      if (clearButton) clearButton.hidden = false;

      let shown = 0;
      let first = null;

      entries.forEach((entry) => {
        let matches = 0;
        entry.files.forEach((file) => {
          const hit = hits(file.haystack, file.squashed);
          file.el.hidden = !hit;
          if (hit) matches += 1;
        });
        entry.groups.forEach((group) => {
          group.el.hidden = !group.rows.some((file) => !file.el.hidden);
        });

        /* A brand with no files of its own is a plain link, so it survives on
           its own name alone. */
        const visible = entry.files.length ? matches > 0 : hits(entry.brand, entry.brandSquashed);
        entry.item.hidden = !visible;
        setOpen(entry, visible && matches > 0);

        if (visible) {
          shown += matches;
          if (!first) first = entry.item;
          if (entry.countEl && entry.files.length) {
            entry.countEl.textContent = countTemplate
              .replace('[n]', String(matches))
              .replace('[t]', String(entry.files.length));
          }
        }
      });

      /* Rules sit between rows, so whichever row is now on top loses its own. */
      entries.forEach((entry) => {
        entry.item.classList.toggle('is-first', entry.item === first);
      });

      if (status) {
        const terse = String(raw).trim();
        status.textContent = shown
          ? resultsTemplate.replace('[n]', String(shown)).replace('[q]', terse)
          : emptyTemplate.replace('[q]', terse);
        status.classList.toggle('doc-search__status--empty', shown === 0);
        status.hidden = false;
      }
    };

    input.addEventListener('input', () => filter(input.value));
    input.addEventListener('search', () => filter(input.value));
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && input.value) {
        event.preventDefault();
        input.value = '';
        reset();
      }
    });
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        input.value = '';
        reset();
        input.focus();
      });
    }

    /* A value can survive a back-navigation or a form restore. */
    if (input.value) filter(input.value);
  };

  /* [data-quote-endpoint] contact form: when the section has an upload endpoint, the form is sent
     there (printersparts.com, which emails it through Resend) so chosen photos arrive as attachments.
     Photos are shrunk in the browser first, because the endpoint accepts 4 MB in total. Without the
     endpoint the form stays a plain Shopify contact form and this does nothing. */
  const QUOTE_MAX_FILES = 5;
  const QUOTE_MAX_BYTES = 4 * 1024 * 1024;
  const QUOTE_MAX_SIDE = 1800;

  const shrinkImage = async (file) => {
    if (!/^image\//.test(file.type) || file.size < 500 * 1024 || !window.createImageBitmap) return file;
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, QUOTE_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
      if (!blob || blob.size >= file.size) return file;
      const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
      return new File([blob], name, { type: 'image/jpeg' });
    } catch (error) {
      /* HEIC outside Safari cannot be decoded here; it is sent as is. */
      return file;
    }
  };

  const initQuoteForm = (form) => {
    const endpoint = form.dataset.quoteEndpoint;
    if (!endpoint || form.dataset.quoteReady) return;
    form.dataset.quoteReady = 'true';

    const startedAt = Date.now();
    const status = form.querySelector('[data-quote-status]');
    const filesInput = form.querySelector('[data-quote-files]');
    const fileList = form.querySelector('[data-quote-file-list]');
    const submit = form.querySelector('[data-quote-submit]');
    const field = (name) => {
      const input = form.elements.namedItem(name);
      return input && typeof input.value === 'string' ? input.value.trim() : '';
    };

    const showStatus = (message, type) => {
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('form-message--success', type === 'success');
      status.classList.toggle('form-message--error', type !== 'success');
      status.hidden = false;
      status.focus({ preventScroll: false });
    };

    if (filesInput && fileList) {
      filesInput.addEventListener('change', () => {
        const names = Array.from(filesInput.files || []).map((file) => file.name);
        fileList.textContent = names.length ? names.join(', ') : '';
        fileList.hidden = !names.length;
      });
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const email = field('contact[email]');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showStatus('Enter a valid email address.', 'error');
        const input = form.elements.namedItem('contact[email]');
        if (input) input.setAttribute('aria-invalid', 'true');
        return;
      }

      const chosen = Array.from((filesInput && filesInput.files) || []);
      if (chosen.length > QUOTE_MAX_FILES) {
        showStatus(`Attach up to ${QUOTE_MAX_FILES} files.`, 'error');
        return;
      }

      if (submit) {
        submit.disabled = true;
        submit.classList.add('is-loading');
      }

      try {
        const files = await Promise.all(chosen.map(shrinkImage));
        if (files.reduce((sum, file) => sum + file.size, 0) > QUOTE_MAX_BYTES) {
          showStatus('The photos are too large. Attach fewer or smaller files.', 'error');
          return;
        }

        const data = new FormData();
        data.append('name', field('contact[name]'));
        data.append('company', field('contact[Company]'));
        data.append('email', email);
        data.append('phone', field('contact[phone]'));
        data.append('press', field('contact[Press or machine]'));
        data.append('serial', field('contact[Serial number]'));
        data.append('message', field('contact[body]'));
        const optIn = form.elements.namedItem('contact[Updates opt-in]');
        if (optIn && optIn.checked) data.append('optIn', 'yes');
        data.append('page', window.location.href);
        data.append('startedAt', String(startedAt));
        const honeypot = form.querySelector('[data-quote-honeypot]');
        data.append('website', honeypot ? honeypot.value : '');
        files.forEach((file) => data.append('photos', file, file.name));

        const response = await fetch(endpoint, { method: 'POST', body: data });
        const result = await response.json().catch(() => ({}));
        if (response.ok && result.ok) {
          form.reset();
          if (fileList) {
            fileList.textContent = '';
            fileList.hidden = true;
          }
          showStatus(form.dataset.quoteSuccess || 'Thanks! Your request has been sent.', 'success');
        } else {
          showStatus(result.message || 'We could not send your request. Please try again or call us.', 'error');
        }
      } catch (error) {
        showStatus('We could not send your request. Please check your connection and try again, or call us.', 'error');
      } finally {
        if (submit) {
          submit.disabled = false;
          submit.classList.remove('is-loading');
        }
      }
    });
  };

  const focusMessages = (scope) => {
    const message = (scope || document).querySelector('[data-focus-on-load]');
    if (message) message.focus({ preventScroll: false });
  };

  const init = (scope) => {
    const root = scope || document;
    root.querySelectorAll('[data-legal]').forEach(initLegal);
    root.querySelectorAll('[data-doc-index]').forEach(initDocIndex);
    root.querySelectorAll('form[data-quote-endpoint]').forEach(initQuoteForm);
  };

  const ready = () => {
    init();
    focusMessages();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-legal]').forEach((root) => {
      if (root._legalObserver) root._legalObserver.disconnect();
    });
  });
})();
